<template>
  <div class="card-block card-block--highlight">
    <div class="block-header block-header--compact">
      <div class="result-title-block">
        <span class="result-title-block__eyebrow">当前阶段</span>
        <h2>{{ currentStageLabel }}</h2>
      </div>
    </div>

    <div class="editor-shell">
      <div v-if="canEditCurrentStage" class="editor-panel">
        <div class="editor-head">
          <strong>正文编辑</strong>
        </div>
        <el-input
          :model-value="editorContent"
          type="textarea"
          :rows="18"
          resize="vertical"
          maxlength="20000"
          show-word-limit
          placeholder="这里会展示当前阶段的可编辑内容"
          @update:model-value="emit('update:editorContent', String($event))"
        />
        <div class="result-actions result-actions--bottom">
          <el-button plain :disabled="!hasUnsavedEdits || saving" @click="emit('cancel-edits')">取消</el-button>
          <el-button type="primary" :loading="saving" :disabled="!hasUnsavedEdits" @click="emit('save-edits')">保存修改</el-button>
        </div>
      </div>

      <div v-else class="preview-panel">
        <div class="editor-head">
          <strong>阅读视图</strong>
          <span>当前阶段暂不支持直接编辑</span>
        </div>
        <div class="result-content result-content--primary">{{ editorContent }}</div>
      </div>
    </div>

    <section v-if="showSavedReviewFeedback" class="saved-feedback-card">
      <span class="saved-feedback-card__eyebrow">已记录的审批意见</span>
      <p>{{ savedReviewFeedback }}</p>
      <small>后续阶段生成会继续携带这条要求。</small>
    </section>

    <section v-if="approvalHistoryRecords.length" class="approval-history-card">
      <div class="approval-history-card__head">
        <span class="saved-feedback-card__eyebrow">审批记录</span>
        <small>按时间倒序展示最近提交的确认与返工意见。</small>
      </div>
      <div class="approval-history-list">
        <article v-for="record in approvalHistoryRecords" :key="record.id" class="approval-history-item">
          <div class="approval-history-item__meta">
            <strong>{{ record.title }}</strong>
            <span>{{ record.timestamp || '刚刚' }}</span>
          </div>
          <p>{{ record.feedback }}</p>
        </article>
      </div>
    </section>

    <section v-if="showApprovalActions" class="inline-approval" :class="{ 'inline-approval--active': pendingApproval || reviewingPreviousStage }">
      <div class="inline-approval__copy">
          <span class="inline-approval__eyebrow">{{ showResumeAction ? '流程暂停' : (pendingApproval ? '待你确认' : (reviewingPreviousStage ? '返回上游修改' : '当前状态')) }}</span>
          <strong>{{ approvalPanelTitle }}</strong>
          <p>{{ approvalPanelSummary }}</p>
      </div>

      <div v-if="showResumeAction" class="inline-approval__actions inline-approval__actions--left">
        <el-button type="primary" :loading="acting" @click="emit('resume')">恢复并继续</el-button>
      </div>

      <div v-else-if="pendingApproval || reviewingPreviousStage" class="inline-approval__form">
        <div class="inline-approval__feedback">
          <span class="inline-approval__feedback-label">审批意见</span>
          <el-input
            :model-value="reviewFeedback"
            type="textarea"
            :rows="4"
            maxlength="1000"
            show-word-limit
            :placeholder="reviewFeedbackPlaceholder"
            @update:model-value="emit('update:reviewFeedback', String($event))"
          />
        </div>

        <div class="inline-approval__command-grid">
          <article class="inline-approval__command inline-approval__command--secondary">
            <div class="inline-approval__command-copy">
              <span class="inline-approval__command-eyebrow">返回修改</span>
              <strong>{{ revisionActionLabel }}</strong>
              <p>{{ revisionActionHint }}</p>
            </div>
            <GenerationModelSplitButton
              v-if="revisionTextOperation"
              class="inline-approval__command-control"
              v-model="revisionTextModelId"
              :action-label="revisionActionLabel"
              :options="revisionTextModelOptions"
              :loading="acting"
              :disabled="!canRequestRevision"
              @trigger="handleRequestRevision"
            />
            <el-button v-else class="inline-approval__command-button" :loading="acting" :disabled="!canRequestRevision" @click="handleRequestRevision">{{ revisionActionLabel }}</el-button>
          </article>

          <article class="inline-approval__command inline-approval__command--primary">
            <div class="inline-approval__command-copy">
              <span class="inline-approval__command-eyebrow">继续流程</span>
              <strong>{{ approveButtonLabel }}</strong>
              <p>{{ approveActionHint }}</p>
            </div>
            <GenerationModelSplitButton
              v-if="approveTextOperation"
              class="inline-approval__command-control"
              v-model="approveTextModelId"
              :action-label="approveButtonLabel"
              :options="approveTextModelOptions"
              :loading="acting"
              :disabled="!canApproveCurrentStage"
              type="primary"
              @trigger="handleApprove"
            />
            <el-button v-else type="primary" class="inline-approval__command-button inline-approval__command-button--primary" :loading="acting" :disabled="!canApproveCurrentStage" @click="handleApprove">{{ approveButtonLabel }}</el-button>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import GenerationModelSplitButton from '@/components/GenerationModelSplitButton.vue'
import type { ModelCatalogItem } from '@/api/config'
import { useModelCatalogStore } from '@/stores/modelCatalog'
import { resolveGenerationModelSelection, setGenerationModelSelection } from '@/utils/generationModelSelection'
import type { ApprovalStage } from '@/types/workflow'
import { getApprovalStageDisplayLabel } from './approvalFlow'

const props = defineProps<{
  currentStageLabel: string
  canEditCurrentStage: boolean
  hasUnsavedEdits: boolean
  saving: boolean
  inspectableStageOptions: Array<{ key: string; label: string }>
  currentStage: ApprovalStage
  previousInspectableStage: string | null
  nextInspectableStage: string | null
  editorHint: string
  editorContent: string
  showApprovalActions: boolean
  pendingApproval: boolean
  pendingApprovalStage: ApprovalStage | null
  reviewingPreviousStage: boolean
  revisionTargetStage: ApprovalStage
  reviewFeedback: string
  savedReviewFeedback: string
  approvalHistoryRecords: Array<{ id: string; stage: string; stageLabel: string; feedback: string; timestamp: string; title: string }>
  sideGuidance: string
  acting: boolean
  canRequestRevision: boolean
  canApproveCurrentStage: boolean
  reviewFeedbackPlaceholder: string
  revisionActionLabel: string
  approveButtonLabel: string
  showResumeAction: boolean
}>()

const emit = defineEmits<{
  (event: 'save-edits'): void
  (event: 'cancel-edits'): void
  (event: 'switch-stage', value: string): void
  (event: 'update:editorContent', value: string): void
  (event: 'update:reviewFeedback', value: string): void
  (event: 'request-revision', payload?: { textModelId?: string }): void
  (event: 'approve', payload?: { textModelId?: string }): void
  (event: 'resume'): void
}>()

const modelCatalogStore = useModelCatalogStore()

const APPROVE_TEXT_OPERATIONS: Partial<Record<ApprovalStage, string>> = {
  proposal: 'workflow_script_generate',
  script: 'workflow_storyboard_generate',
}

const REVISION_TEXT_OPERATIONS: Partial<Record<ApprovalStage, string>> = {
  proposal: 'workflow_proposal_generate',
  script: 'workflow_script_generate',
  storyboard: 'workflow_storyboard_generate',
}

const approveTextModelId = ref('')
const revisionTextModelId = ref('')

const approveTextOperation = computed(() => APPROVE_TEXT_OPERATIONS[props.currentStage] || '')
const revisionTextOperation = computed(() => {
  const actionableStage = props.pendingApprovalStage || props.currentStage
  return REVISION_TEXT_OPERATIONS[actionableStage] || ''
})

function normalizeFeedbackText(value: unknown) {
  return String(value || '').replace(/\s+/g, ' ').trim()
}

const showSavedReviewFeedback = computed(() => {
  if (props.approvalHistoryRecords.length) {
    return false
  }

  const feedback = normalizeFeedbackText(props.savedReviewFeedback)
  if (!feedback) {
    return false
  }

  const latestHistoryFeedback = normalizeFeedbackText(props.approvalHistoryRecords[0]?.feedback)
  return !latestHistoryFeedback || latestHistoryFeedback !== feedback
})
const currentStageDisplayLabel = computed(() => getApprovalStageDisplayLabel(props.currentStage))
const approvalPanelTitle = computed(() => {
  if (props.showResumeAction) {
    return '恢复当前流程'
  }
  if (props.pendingApproval) {
    return `${currentStageDisplayLabel.value}确认`
  }
  if (props.reviewingPreviousStage) {
    return `${currentStageDisplayLabel.value}重新确认`
  }
  return '流程状态'
})
const approvalPanelSummary = computed(() => {
  if (props.showResumeAction) {
    return props.sideGuidance
  }
  if (props.pendingApproval) {
    return '确认后进入下一阶段；如需返工，可写清修改要求后返回上一阶段。'
  }
  if (props.reviewingPreviousStage) {
    return `当前先展示${currentStageDisplayLabel.value}旧结果；你确认后，系统才会重生成后续阶段。`
  }
  return props.sideGuidance
})
const approveActionHint = computed(() => {
  if (props.reviewingPreviousStage) {
    return `确认这一版${currentStageDisplayLabel.value}后，系统才会继续刷新后续阶段。`
  }
  return `确认当前${currentStageDisplayLabel.value}后，流程会继续推进到下一阶段。`
})
const revisionActionHint = computed(() => {
  if (props.reviewingPreviousStage) {
    return `系统会基于你当前输入的要求，重新生成${currentStageDisplayLabel.value}。`
  }
  return `把这轮意见带回上游处理，再回来重新确认${currentStageDisplayLabel.value}。`
})

const approveTextModelOptions = computed(() => {
  if (!approveTextOperation.value) {
    return []
  }
  return modelCatalogStore.textModelsForOperation(approveTextOperation.value)
})

const revisionTextModelOptions = computed(() => {
  if (!revisionTextOperation.value) {
    return []
  }
  return modelCatalogStore.textModelsForOperation(revisionTextOperation.value)
})

function syncSelectedModel(
  target: typeof approveTextModelId,
  operation: string,
  options: ModelCatalogItem[]
) {
  target.value = operation ? resolveGenerationModelSelection('text', operation, options) : ''
}

watch(
  [approveTextOperation, approveTextModelOptions],
  ([operation, options]) => {
    syncSelectedModel(approveTextModelId, operation, options)
  },
  { immediate: true }
)

watch(
  [revisionTextOperation, revisionTextModelOptions],
  ([operation, options]) => {
    syncSelectedModel(revisionTextModelId, operation, options)
  },
  { immediate: true }
)

watch(approveTextModelId, (value) => {
  if (approveTextOperation.value) {
    setGenerationModelSelection('text', approveTextOperation.value, value)
  }
})

watch(revisionTextModelId, (value) => {
  if (revisionTextOperation.value) {
    setGenerationModelSelection('text', revisionTextOperation.value, value)
  }
})

onMounted(() => {
  void modelCatalogStore.ensureLoaded()
})

function handleApprove() {
  emit('approve', { textModelId: approveTextModelId.value || undefined })
}

function handleRequestRevision() {
  emit('request-revision', { textModelId: revisionTextModelId.value || undefined })
}
</script>

<style scoped>
.card-block,
.editor-panel,
.preview-panel {
  padding: 24px;
  border-radius: 24px;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.05);
}

.card-block--highlight {
  background: linear-gradient(180deg, rgba(255, 247, 237, 0.82), rgba(255, 255, 255, 0.98));
}

.block-header,
.editor-head,
.result-actions,
.editor-shell,
.saved-feedback-card {
  display: flex;
  gap: 14px;
}

.block-tip {
  color: #c2410c;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 700;
}

.block-header--compact {
  align-items: center;
}

.result-title-block {
  display: grid;
  gap: 6px;
}

.result-title-block__eyebrow {
  color: #b45309;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 700;
}

.result-title-block__hint {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.block-header,
.editor-head {
  justify-content: space-between;
}

.block-header h2,
.editor-head strong {
  color: #0f172a;
}

.editor-head span {
  color: #64748b;
  font-size: 12px;
}

.editor-shell {
  margin-top: 18px;
}

.saved-feedback-card {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid rgba(245, 158, 11, 0.2);
  background: rgba(255, 251, 235, 0.9);
  flex-direction: column;
}

.saved-feedback-card__eyebrow {
  color: #b45309;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.saved-feedback-card p,
.saved-feedback-card small {
  margin: 0;
}

.saved-feedback-card p {
  color: #7c2d12;
  font-size: 14px;
  line-height: 1.7;
}

.saved-feedback-card small {
  color: #9a3412;
  font-size: 12px;
}

.approval-history-card {
  margin-top: 18px;
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(248, 250, 252, 0.9);
}

.approval-history-card,
.approval-history-card__head,
.approval-history-list,
.approval-history-item {
  display: grid;
  gap: 12px;
}

.approval-history-card__head small {
  color: #64748b;
  font-size: 12px;
}

.approval-history-list {
  max-height: 280px;
  overflow: auto;
}

.approval-history-item {
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.approval-history-item__meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: baseline;
}

.approval-history-item__meta strong {
  color: #0f172a;
  font-size: 14px;
}

.approval-history-item__meta span {
  color: #64748b;
  font-size: 12px;
}

.approval-history-item p {
  margin: 0;
  color: #334155;
  line-height: 1.7;
  white-space: pre-wrap;
}

.editor-panel,
.preview-panel {
  flex: 1;
  margin-top: 18px;
  min-height: 560px;
}

.editor-panel,
.preview-panel {
  display: flex;
  flex-direction: column;
  background: rgba(248, 250, 252, 0.9);
}

:deep(.editor-panel .el-textarea) {
  flex: 1;
  width: 100%;
}

:deep(.editor-panel .el-textarea__inner) {
  width: 100%;
  height: 100% !important;
  min-height: 420px !important;
}

.result-content {
  color: #475569;
  font-size: 15px;
  line-height: 1.75;
  white-space: pre-wrap;
}

.result-actions--bottom {
  margin-top: 16px;
  justify-content: flex-end;
}

.inline-approval {
  display: grid;
  gap: 18px;
  margin-top: 20px;
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(248, 250, 252, 0.96));
  border: 1px solid rgba(226, 232, 240, 0.94);
  box-shadow: 0 22px 48px rgba(15, 23, 42, 0.06);
}

.inline-approval--active {
  background:
    radial-gradient(circle at top right, rgba(251, 191, 36, 0.14), transparent 22%),
    linear-gradient(180deg, rgba(255, 251, 235, 0.98), rgba(255, 255, 255, 0.98));
  border-color: rgba(245, 158, 11, 0.28);
}

.inline-approval__copy,
.inline-approval__form {
  display: grid;
  gap: 14px;
}

.inline-approval__eyebrow {
  color: #b45309;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.inline-approval__copy strong {
  color: #0f172a;
  font-size: 26px;
  letter-spacing: -0.03em;
}

.inline-approval__copy p {
  margin: 0;
  color: #64748b;
  line-height: 1.75;
}

.inline-approval__feedback {
  display: grid;
  gap: 10px;
  padding: 16px 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(226, 232, 240, 0.94);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.84);
}

.inline-approval__feedback-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.inline-approval__command-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.inline-approval__actions--left {
  justify-content: flex-start;
}

.inline-approval__command {
  display: grid;
  gap: 14px;
  align-content: start;
  padding: 18px;
  border-radius: 24px;
  border: 1px solid rgba(226, 232, 240, 0.94);
  background: rgba(255, 255, 255, 0.84);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.04);
}

.inline-approval__command--primary {
  background:
    radial-gradient(circle at top right, rgba(96, 165, 250, 0.16), transparent 24%),
    linear-gradient(180deg, rgba(239, 246, 255, 0.96), rgba(255, 255, 255, 0.94));
  border-color: rgba(191, 219, 254, 0.92);
}

.inline-approval__command--secondary {
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.94));
}

.inline-approval__command-copy {
  display: grid;
  gap: 8px;
}

.inline-approval__command-eyebrow {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.inline-approval__command-copy strong {
  color: #0f172a;
  font-size: 24px;
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.inline-approval__command-copy p {
  margin: 0;
  color: #64748b;
  line-height: 1.7;
}

.inline-approval__command-control,
.inline-approval__command-button {
  width: 100%;
}

.inline-approval__command-button {
  min-height: 58px;
  border-radius: 18px;
  font-weight: 800;
}

.inline-approval__command-button--primary {
  box-shadow: 0 14px 28px rgba(37, 99, 235, 0.16);
}

@media (max-width: 1080px) {
  .inline-approval__command-grid {
    grid-template-columns: 1fr;
  }
}
</style>